// Конвертира число в българско словесно описание — ползва се за
// „Обща стойност словом" в фактурата. Поддържа суми до 999_999_999.99.
//
// Примери:
//   moneyToWordsBg(0)      → "нула евро"
//   moneyToWordsBg(1)      → "едно евро"
//   moneyToWordsBg(1.01)   → "едно евро и един евроцент"
//   moneyToWordsBg(66.19)  → "шестдесет и шест евро и деветнадесет евроцента"
//
// Граматически бележки:
//   - „евро" е inavriant (1 евро, 100 евро) и среден род → числото за
//     евро ползва ONES_N ("едно евро").
//   - „евроцент" е мъжки род → ползваме ONES_M ("един евроцент").
//   - „хиляда" е женски род → recursion за хиляди ползва ONES_F.
//   - „милион" е мъжки род → recursion за милиони ползва ONES_M.

const ONES_M = [
  "нула",
  "един",
  "два",
  "три",
  "четири",
  "пет",
  "шест",
  "седем",
  "осем",
  "девет",
];

const ONES_F = [
  "нула",
  "една",
  "две",
  "три",
  "четири",
  "пет",
  "шест",
  "седем",
  "осем",
  "девет",
];

const ONES_N = [
  "нула",
  "едно",
  "две",
  "три",
  "четири",
  "пет",
  "шест",
  "седем",
  "осем",
  "девет",
];

const TEENS = [
  "десет",
  "единадесет",
  "дванадесет",
  "тринадесет",
  "четиринадесет",
  "петнадесет",
  "шестнадесет",
  "седемнадесет",
  "осемнадесет",
  "деветнадесет",
];

const TENS = [
  "",
  "",
  "двадесет",
  "тридесет",
  "четиридесет",
  "петдесет",
  "шестдесет",
  "седемдесет",
  "осемдесет",
  "деветдесет",
];

const HUNDREDS = [
  "",
  "сто",
  "двеста",
  "триста",
  "четиристотин",
  "петстотин",
  "шестстотин",
  "седемстотин",
  "осемстотин",
  "деветстотин",
];

type Gender = "m" | "f" | "n";

function intToWordsBg(n: number, gender: Gender): string {
  if (n === 0) return "";
  const parts: string[] = [];

  const millions = Math.floor(n / 1_000_000);
  if (millions > 0) {
    if (millions === 1) parts.push("един милион");
    else parts.push(intToWordsBg(millions, "m") + " милиона");
    n %= 1_000_000;
  }

  const thousands = Math.floor(n / 1000);
  if (thousands > 0) {
    if (thousands === 1) parts.push("хиляда");
    else if (thousands === 2) parts.push("две хиляди");
    else parts.push(intToWordsBg(thousands, "f") + " хиляди");
    n %= 1000;
  }

  if (n > 0) {
    const last3: string[] = [];
    const hundreds = Math.floor(n / 100);
    const last2 = n % 100;
    if (hundreds > 0) last3.push(HUNDREDS[hundreds]);
    if (last2 > 0) {
      const ones = gender === "f" ? ONES_F : gender === "n" ? ONES_N : ONES_M;
      if (last2 < 10) {
        last3.push(ones[last2]);
      } else if (last2 < 20) {
        last3.push(TEENS[last2 - 10]);
      } else {
        const tens = Math.floor(last2 / 10);
        const units = last2 % 10;
        if (units > 0) {
          last3.push(TENS[tens] + " и " + ones[units]);
        } else {
          last3.push(TENS[tens]);
        }
      }
    }
    // Между „сто" / „двеста" и десетиците/единиците в български се слага „и".
    parts.push(last3.join(" и "));
  }

  return parts.join(" и ");
}

export function moneyToWordsBg(amount: number): string {
  if (!Number.isFinite(amount)) return "";
  const sign = amount < 0 ? "минус " : "";
  amount = Math.abs(amount);
  const euros = Math.floor(amount);
  // Math.round за да избегнем floating-point дребни — 66.19 е 6618.99999 в float.
  const cents = Math.round((amount - euros) * 100);

  // „евро" е invariant и от среден род → ползваме ONES_N („едно евро").
  const eurosPart = euros === 0 ? "нула" : intToWordsBg(euros, "n");
  const eurosUnit = "евро";

  if (cents === 0) {
    return `${sign}${eurosPart} ${eurosUnit}`;
  }

  // „евроцент" е мъжки род → ползваме ONES_M („един евроцент").
  const centsWords = intToWordsBg(cents, "m");
  const centsUnit = cents === 1 ? "евроцент" : "евроцента";

  return `${sign}${eurosPart} ${eurosUnit} и ${centsWords} ${centsUnit}`;
}
